import React, { useState } from 'react';
import { ShieldCheckIcon, LockClosedIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, usePagination } from '../../hooks';
import { authAPI, adminAPI, usersAPI } from '../../services/api';
import {
  Card, SectionHeader, Button, Badge, Pagination, LoadingPage,
  EmptyState, Modal, Input, StatusBadge,
} from '../../components/common/UI';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const ROLES = [
  { role: 'super_admin',    color: 'red',    level: 'Full system access including token blocking',      permissions: ['All permissions', 'Block tokens', 'Manage admins', 'Delete data'] },
  { role: 'admin',          color: 'purple', level: 'Full management except token blocking',            permissions: ['Manage users', 'Approve businesses', 'Manage orders', 'Manage products', 'View analytics'] },
  { role: 'moderator',      color: 'blue',   level: 'Content moderation + read-only access',           permissions: ['Moderate reviews', 'Read all orders', 'View users', 'Manage chat', 'Manage returns'] },
  { role: 'business_owner', color: 'yellow', level: 'Own business products, orders, coupons',          permissions: ['Create/edit products', 'Manage inventory', 'View business orders', 'Create coupons', 'Manage staff'] },
  { role: 'business_staff', color: 'yellow', level: 'Limited inventory & order updates',               permissions: ['Update inventory', 'Update order status', 'View orders'] },
  { role: 'customer',       color: 'gray',   level: 'Shop, order, return, review, chat',               permissions: ['Browse products', 'Place orders', 'Request returns', 'Write reviews', 'Chat support'] },
];

const ROLE_BADGE = { super_admin:'red', admin:'purple', moderator:'blue', business_owner:'yellow', business_staff:'yellow', customer:'gray' };

export function RolesPage() {
  const [blockModal, setBlockModal] = useState(false);
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('admin_revoke');

  const { mutate: blockTokens, loading: blocking } = useMutation(
    () => authAPI.blockTokens({ userId, reason }),
    {
      successMsg: 'All tokens blocked for user',
      onSuccess: () => { setBlockModal(false); setUserId(''); },
    }
  );

  return (
    <div className="animate-in space-y-5">
      <SectionHeader
        title="Roles & Permissions"
        subtitle="Role-based access control system"
        action={
          <Button variant="danger" size="sm" icon={LockClosedIcon} onClick={() => setBlockModal(true)}>
            Block User Tokens
          </Button>
        }
      />

      {/* Role cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {ROLES.map(({ role, color, level, permissions }) => (
          <Card key={role} className="p-5 hover:shadow-card-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-100">
                <ShieldCheckIcon className="h-5 w-5 text-surface-600" />
              </div>
              <Badge variant={ROLE_BADGE[role]}>{role.replace(/_/g,' ')}</Badge>
            </div>
            <h3 className="font-display font-semibold text-surface-900 capitalize">{role.replace(/_/g,' ')}</h3>
            <p className="mt-1 text-xs text-surface-500">{level}</p>
            <div className="mt-4 space-y-1.5">
              {permissions.map(p => (
                <div key={p} className="flex items-center gap-2 text-xs text-surface-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-success-400 flex-shrink-0" />
                  {p}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Role hierarchy */}
      <Card>
        <h2 className="font-display font-semibold text-surface-900 mb-4">Role Hierarchy</h2>
        <div className="space-y-2">
          {ROLES.map(({ role, color }, i) => (
            <div key={role} className="flex items-center gap-3">
              <span className="w-6 text-xs font-bold text-surface-400 text-right">{ROLES.length - i}</span>
              <div className="flex-1 rounded-xl bg-surface-50 px-4 py-2.5 flex items-center justify-between">
                <span className="text-sm font-medium text-surface-700 capitalize">{role.replace(/_/g,' ')}</span>
                <div className="w-48 bg-surface-200 rounded-full h-1.5 overflow-hidden">
                  <div className="bg-brand-500 h-1.5 rounded-full" style={{ width: `${((ROLES.length - i) / ROLES.length) * 100}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Block token modal */}
      <Modal open={blockModal} onClose={() => setBlockModal(false)} title="Block User Tokens"
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" size="sm" onClick={() => setBlockModal(false)}>Cancel</Button><Button variant="danger" size="sm" loading={blocking} onClick={blockTokens}>Block All Tokens</Button></div>}>
        <div className="space-y-4">
          <p className="text-sm text-surface-600">This will immediately invalidate all JWT tokens for the user. They will be logged out of all devices.</p>
          <Input label="User ID" value={userId} onChange={e => setUserId(e.target.value)} placeholder="Enter user UUID…" />
          <div>
            <label className="label">Reason</label>
            <select className="input" value={reason} onChange={e => setReason(e.target.value)}>
              <option value="admin_revoke">Admin Revoke</option>
              <option value="account_suspended">Account Suspended</option>
              <option value="suspicious_activity">Suspicious Activity</option>
              <option value="password_change">Password Change</option>
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function TokenManagerPage() {
  const [userId, setUserId] = useState('');
  const { page, setPage } = usePagination();

  const { data, loading, refetch } = useQuery(
    () => adminAPI.blockedTokens({ page, limit: 20, user_id: userId || undefined }),
    [page, userId]
  );

  const { mutate: blockAll, loading: blocking } = useMutation(
    () => authAPI.blockTokens({ userId, reason: 'admin_revoke' }),
    { successMsg: 'Tokens blocked', onSuccess: () => { refetch(); setUserId(''); } }
  );

  const tokens = Array.isArray(data) ? data : (data?.rows || data?.data || []);
  const total  = data?.pagination?.total || data?.count || data?.total || tokens.length || 0;
  const pages  = data?.pagination?.pages || (data?.limit ? Math.ceil(total / data.limit) : Math.ceil(total / 20) || 1);

  const REASON_BADGE = {
    logout: 'gray', password_change: 'blue', admin_revoke: 'purple',
    suspicious_activity: 'red', account_suspended: 'red',
  };

  return (
    <div className="animate-in space-y-5">
      <SectionHeader title="Token Manager" subtitle="View and manage blocked JWT tokens" />

      {/* Block form */}
      <Card>
        <h3 className="font-display font-semibold text-surface-900 mb-4">Revoke User Tokens</h3>
        <div className="flex items-end gap-3">
          <Input label="User ID" value={userId} onChange={e => setUserId(e.target.value)} placeholder="Enter user UUID…" className="flex-1 max-w-sm" />
          <Button variant="danger" size="md" icon={LockClosedIcon} loading={blocking} disabled={!userId.trim()} onClick={blockAll}>
            Revoke All
          </Button>
        </div>
        <p className="mt-2 text-xs text-surface-400">This logs the user out of all devices immediately via Redis blocklist + DB audit trail.</p>
      </Card>

      {/* Blocked tokens table */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-surface-900">Active Blocklist ({total})</h3>
        </div>
        {loading ? <LoadingPage /> : tokens.length === 0 ? (
          <EmptyState icon={ShieldCheckIcon} title="No blocked tokens" description="All tokens are currently valid" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>JTI</th><th>User</th><th>Type</th><th>Reason</th><th>Expires</th><th>Blocked At</th></tr></thead>
              <tbody>
                {tokens.map(t => (
                  <tr key={t.id}>
                    <td><span className="font-mono text-xs text-surface-400">{t.jti?.substring(0,20)}…</span></td>
                    <td><span className="font-mono text-xs">{t.user_id?.substring(0,12)}…</span></td>
                    <td><Badge variant="gray">{t.token_type}</Badge></td>
                    <td><Badge variant={REASON_BADGE[t.reason] || 'gray'}>{t.reason?.replace(/_/g,' ')}</Badge></td>
                    <td className="text-xs text-surface-400">{t.expires_at ? format(new Date(t.expires_at), 'dd MMM yy HH:mm') : '—'}</td>
                    <td className="text-xs text-surface-400">{t.created_at ? format(new Date(t.created_at), 'dd MMM yy HH:mm') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}
